'use client'

import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'
import Link from 'next/link'
import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'

import type { Post } from '@/payload-types'
import { Media } from '@/components/Media'

export type CardPostData = Pick<
  Post,
  'slug' | 'categories' | 'meta' | 'title' | 'publishedAt' | 'createdAt'
>

export const Card: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  relationTo?: 'posts'
  showCategories?: boolean
  title?: string
  revealDelay?: number
}> = (props) => {
  const { card, link } = useClickableCard({})
  const {
    className,
    doc,
    relationTo,
    showCategories,
    title: titleFromProps,
    revealDelay = 0,
  } = props

  const { slug, categories, meta, title } = doc || {}
  const { image: metaImage } = meta || {}

  const reduceMotion = useReducedMotion()

  const hasCategories =
    categories && Array.isArray(categories) && categories.length > 0
  const titleToUse = titleFromProps || title
  // const href = `/${relationTo}/${slug}`
  const href = relationTo === 'posts' ? `/news/${slug}` : `/${relationTo}/${slug}`

  return (
    <motion.article
      ref={card.ref}
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={
        reduceMotion ? undefined : { duration: 0.6, ease: 'easeOut', delay: revealDelay }
      }
      className={cn('group relative', className)}
    >
      
    {/* Image: tall portrait ratio + true cover */}
    {/* Image: tall portrait ratio + true cover + hover zoom */}
    <Link
      href={href}
      ref={link.ref}
      aria-label={`Read ${titleToUse ?? 'post'}`}
      className="block relative overflow-hidden rounded-2xl group"
    >
      <div className="relative w-full overflow-hidden rounded-2xl">
        {/* keep one tall aspect across all cards */}
        <div className="aspect-[3/4] w-full" />

        {/* absolutely fill + zoom wrapper */}
        <div className="absolute inset-0 card-media overflow-hidden">
          <div
            className="
              h-full w-full
              transition-transform duration-700 ease-out
              group-hover:scale-[1.05]
              motion-reduce:transition-none motion-reduce:transform-none
            "
          >
            {/* If Media forwards className to <img>, this ensures object-cover */}
            <Media resource={metaImage} size="100vw" className="h-full w-full object-cover" />
          </div>
        </div>
      </div>
    </Link>


      {/* Title under image */}
      {titleToUse && (
        <h3 className="mt-4 font-serif text-xl md:text-2xl leading-snug font-medium text-foreground">
          <Link
            href={href}
            className="relative after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:ring-2 rounded-sm"
          >
            <span
              className={cn(
                'relative inline pb-0.5 [box-decoration-break:clone]',
                '[background-image:linear-gradient(currentColor,currentColor)]',
                '[background-repeat:no-repeat]',
                '[background-position:0_100%]',
                '[background-size:0%_2px]',
                'transition-[background-size] duration-[800ms] ease-out',
                'group-hover:[background-size:100%_2px] hover:[background-size:100%_2px] focus-visible:[background-size:100%_2px]',
                'motion-reduce:transition-none'
              )}
            >
              {titleToUse}
            </span>
          </Link>
        </h3>
      )}


    </motion.article>
  )
}
